/**
 * The sign-in gate's artwork — **client review C5**, 2026-08-22.
 *
 * IFDC supplied the photograph, the Kingdom of the Netherlands wordmark, the Soil Values logo
 * and eight coalition partner marks. They are resolved here rather than imported at the point
 * of use, for one reason: **a missing file must not be a build failure.** A static
 * `import backdrop from '.../backdrop.jpg'` is a hard dependency — remove the file, or hand
 * this repository to someone who cannot redistribute the photograph, and the bundle stops
 * compiling. `import.meta.glob` asks the same question and answers `{}`, so every consumer
 * below degrades to a CSS gradient or a styled text mark and the page still renders.
 *
 * The globs are eager and `?url`: Vite hashes each file into `dist/assets` at build time and
 * hands back the URL, so nothing is fetched that the page does not use and the CSP-safe
 * single-origin rule is kept (no CDN, no external host).
 *
 * ## `backdrop.jpg` is a derivative, and the original is not in the bundle
 *
 * The delivered photograph is a 3384×4400 PNG — 12.9 MB, on the **first screen of every
 * session**, for a portal whose field users are in Burkina Faso, Mali, Niger and Nigeria.
 * The left panel is never wider than ~720 CSS px, so that was 12.6 MB nobody sees. What ships
 * is a 1400 px JPEG derived from it: 212 kB, the same picture at the size it is displayed.
 *
 * The original is **not** kept beside it, and that is the point worth writing down: an eager
 * `?url` glob emits every file it matches into `dist/assets`, used or not, so keeping the PNG
 * "as the source of truth" put 12.9 MB into the built bundle that no browser would ever
 * request. A source of truth belongs in IFDC's asset store, not in a web bundle. Replace
 * `backdrop.jpg` with a better derivative any time; the glob does not care which file it is.
 *
 * ## …and so is every other file here
 *
 * The backdrop argument was made for the backdrop and then applied to one file out of eleven.
 * The other ten shipped at the resolution IFDC delivered them at — 783 kB of logo artwork on
 * the first screen of every session, for marks that `base.css` paints into a cell no taller
 * than 60 px and ~140 px wide, and a 118 px logo. Landscape Alliance alone was 2560×650 /
 * 268 kB to be drawn 150 px wide. `loading="lazy"` bought nothing and is not used: the partner
 * row is above the fold on the landing screen.
 *
 * They are now derivatives too, at ~2× their painted box, and the whole set is 73 kB. Each was
 * re-encoded three or four ways (optimised RGBA PNG, 255-colour palette PNG, lossless WebP,
 * WebP q85) and the smallest was kept, with two rules that stop the optimisation eating the
 * artwork or the diff: a lossy candidate is only eligible when its RMS difference from the
 * straight LANCZOS resize — measured **composited onto the white card these sit on**, since the
 * RGB under a transparent pixel is undefined and every encoder rewrites it — is under 3/255;
 * and a candidate in a different container has to beat the best same-extension one by 15 % to
 * be worth the rename. (`landscape-alliance` was that one changed extension until 2026-09-02,
 * when IFDC supplied a replacement logo as a png, downscaled to 132px tall at ingest.)
 * `isric.png` was left alone: at 322×85 against a 300×76 box it was already at target.
 *
 * The mixed extensions are therefore a result, not an oversight. This module is why they cost
 * nothing: the globs below match `{png,jpg,jpeg,webp,svg}` and key on the file *stem*, so a
 * better derivative in any format can replace any of these without a line changing here.
 *
 * ## The partner row is ordered here, not by the filesystem
 *
 * `import.meta.glob` returns paths in an order nobody should depend on. C5 names the eight
 * marks in a specific sequence, so the sequence is a constant and the glob only supplies URLs
 * for it. A partner whose file is absent keeps its slot as a text mark — the row is the
 * coalition, and a coalition with a silent gap in it is worse than one drawn in type.
 *
 * ## 2026-09-08, client specification S1: the files were trimmed, and each mark is a link
 *
 * Two changes, both driven by `docs/client-specs/2026-09-08-entry-s1.md`.
 *
 * **The files carry no dead border any more.** `scripts/trim-logos.py` cropped every one of
 * them to the box a reader can actually see when the mark sits on the band's white card, and
 * kept the delivered file in `partners/originals/`. This is the half of "make them the same
 * size" that CSS cannot do: `object-fit:contain` scales a file's *bounding box*, so 42 px of
 * white above the Landscape Alliance lockup and 40 px below it meant that mark was drawn at
 * 38 % of the height its neighbours got, in a cell the same size as theirs. The dimensions in
 * `fit` below are the trimmed ones.
 *
 * **Every mark is now an outbound link.** The specification: *"clicking a logo opens that
 * partner's website in a new tab"*. Each `href` below was checked on 2026-09-08 — every one
 * answers 200, and the two that redirect are recorded at their entry rather than silently
 * followed, because a link that costs the reader a hop is a link with the wrong address in it.
 *
 * ## `fit` is the eye-balance, and it is a property of the artwork
 *
 * The cell is one fixed size for all eight. That alone does not make them *look* the same
 * size: contained inside a 2.4:1 cell, a 5.2:1 wordmark comes out 24 px tall and a 0.9:1
 * square mark comes out 48 px tall and twice as dense. So each mark declares how much of the
 * cell's height it may take, banded by its own aspect ratio — wide wordmarks are already
 * width-bound and take all of it, and the squarer and denser a mark is the more it gives back.
 * The numbers live in `base.css` (`--fit`), keyed on the `data-fit` this field renders.
 */

type UrlMap = Record<string, string>;

const first = (...maps: UrlMap[]): string | null => {
  for (const map of maps) {
    const url = Object.values(map)[0];
    if (url) return url;
  }
  return null;
};

/*
 * Every `import.meta.glob` below repeats its options rather than sharing a constant: Vite
 * resolves the call at transform time, not at runtime, so the second argument has to be an
 * inline object literal it can read statically. `{ eager, query: '?url', import: 'default' }`
 * is "give me the hashed URLs now, not a promise per file".
 */

/**
 * The left panel's photograph; absent ⇒ `.gate-art-wash`'s gradient *is* the panel. Since
 * 2026-10-06 a client-supplied field photo (a farmer preparing a plot). Since 2026-10-08 an
 * AVIF derivative, 189 kB against the JPEG's 434 — AVIF is the one format in this module's own
 * `{png,jpg,jpeg,webp,svg}` lists that isn't tried elsewhere, so it is its own glob, tried
 * first, rather than one more extension in the backdrop's own list: that list has no fixed
 * precedence between its members (a directory with both a `.jpg` and a `.webp` would hand back
 * whichever `import.meta.glob` happened to list first, undocumented and not meant to be relied
 * on — the same reason `PARTNER_ORDER` exists below), and the smaller file is the one worth
 * being deliberate about on the portal's first screen. `first()`'s own fallback order still
 * applies behind it, so a backdrop shipped as JPEG/PNG/WebP alone keeps working unchanged.
 */
export const BACKDROP: string | null = first(
  import.meta.glob('../../assets/gate/backdrop.avif', {
    eager: true,
    query: '?url',
    import: 'default',
  }) as UrlMap,
  import.meta.glob('../../assets/gate/backdrop.{jpg,jpeg,png,webp}', {
    eager: true,
    query: '?url',
    import: 'default',
  }) as UrlMap,
);


/** The donor wordmark, the whole of the band's *Funded by* group. */
export const NETHERLANDS: string | null = first(
  import.meta.glob('../../assets/gate/netherlands.{png,jpg,jpeg,webp,svg}', {
    eager: true,
    query: '?url',
    import: 'default',
  }) as UrlMap,
);

/**
 * The donor's own site. `government.nl` is the Kingdom's English-language portal and the
 * address the wordmark stands for; verified 200 on 2026-09-08.
 */
export const NETHERLANDS_HREF = 'https://www.government.nl/';

/**
 * The Soil Values mark over the form. `/logo.png` — the topbar's, served from `public/` — is
 * the last resort rather than a `null`, because this one identifies the product: a gate with
 * no logo at all reads as a page that failed to load.
 */
export const LOGO: string =
  first(
    import.meta.glob('../../assets/gate/soil-values-logo.{png,jpg,jpeg,webp,svg}', {
      eager: true,
      query: '?url',
      import: 'default',
    }) as UrlMap,
  ) ?? '/logo.png';

/**
 * How much of the cell's height a mark may take, banded by its trimmed aspect ratio. The
 * fractions themselves are in `base.css`; this is the band each mark falls in.
 *
 * * `wide` — 3:1 and wider. Already bound by the cell's width, so it takes the full height it
 *   can reach and never gets near the cap.
 * * `mid` — 2:1 to 3:1. Height-bound, and left uncapped it would stand a third taller than the
 *   wordmarks beside it.
 * * `narrow` — 1.4:1 to 2:1.
 * * `compact` — squarer than 1.4:1. The specification's own instruction: 70–80 % of the cell,
 *   because a small solid mark reads heavier than the box it occupies.
 */
export type MarkFit = 'wide' | 'mid' | 'narrow' | 'compact';

export interface PartnerMark {
  /** File stem, and the `data-partner` hook. */
  slug: string;
  /** The text mark drawn when there is no file — also every logo's `alt`. */
  name: string;
  /** The partner's own site, opened in a new tab. Verified 2026-09-08. */
  href: string;
  /** The eye-balance band; rendered as `data-fit`. */
  fit: MarkFit;
  url: string | null;
}

/**
 * C5's order, top to bottom of the client's mock, left to right in the row — and the same
 * order IFDC's brand guide gives the band the gate now draws (docs/NOTES.md, 2026-09-02).
 *
 * The guide ends with **CIFOR and World Agroforestry as two marks**. They merged, and the
 * single mark IFDC delivered — Landscape Alliance — is the merged organisation rather than a
 * placeholder for two missing files: `cifor-icraf.org` itself now redirects to
 * `landscapealliance.org`, whose own strapline is "CIFOR & ICRAF in action". So the guide's
 * nine names are eight marks here, and that is the finished state, not a gap. Confirmed by the
 * client, 2026-09-08.
 *
 * The aspect ratios in the comments are the **trimmed** files (`scripts/trim-logos.py`), which
 * is what `fit` is chosen against; re-run the script and re-check the band after any logo is
 * replaced.
 */
const PARTNER_ORDER: readonly Omit<PartnerMark, 'url'>[] = [
  // 236×62 — 3.8:1
  { slug: 'ifdc', name: 'IFDC', href: 'https://ifdc.org/', fit: 'wide' },
  // 49×54 — 0.9:1, and a solid filled mark: the one the 70–80 % rule was written for.
  { slug: 'snv', name: 'SNV', href: 'https://www.snv.org/', fit: 'compact' },
  // 280×54 — 5.2:1, the widest of the eight.
  { slug: 'wageningen', name: 'Wageningen UR', href: 'https://www.wur.nl/', fit: 'wide' },
  // 193×76 — 2.5:1
  { slug: 'agra', name: 'AGRA', href: 'https://agra.org/', fit: 'mid' },
  // 103×47 — 2.2:1
  { slug: 'iita', name: 'IITA', href: 'https://iita.org/', fit: 'mid' },
  // 107×62 — 1.7:1. `iwmi.cgiar.org` 301s to `iwmi.org`; the destination is linked directly.
  { slug: 'iwmi', name: 'IWMI', href: 'https://www.iwmi.org/', fit: 'narrow' },
  // 322×85 — 3.8:1
  { slug: 'isric', name: 'ISRIC', href: 'https://isric.org/', fit: 'wide' },
  // 157×50 after trimming — 3.1:1. `cifor-icraf.org` 302s to `landscapealliance.org`, which is
  // the merged organisation's own site; the destination is linked directly.
  {
    slug: 'landscape-alliance',
    name: 'Landscape Alliance',
    href: 'https://www.landscapealliance.org/',
    fit: 'wide',
  },
];

const partnerFiles = import.meta.glob('../../assets/gate/partners/*.{png,jpg,jpeg,webp,svg}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as UrlMap;

const partnerBySlug = new Map(
  Object.entries(partnerFiles).map(([path, url]) => [
    path.split('/').pop()!.replace(/\.[^.]+$/, ''),
    url,
  ]),
);

export const PARTNERS: readonly PartnerMark[] = PARTNER_ORDER.map((mark) => ({
  ...mark,
  url: partnerBySlug.get(mark.slug) ?? null,
}));
